<template>
  <div class="p-4">
    <h2 class="my-4 heading-1">
      Restore Deleted Sizings, Scenarios & Performance Evaluations
    </h2>
    <SizingsAdvancedSearch
      v-model:sizings-search="sizingsSearch"
      class="mb-4"
    />
    <div class="flex justify-content-between align-items-center">
      <h2>Deleted Sizings</h2>
      <Button
        :disabled="sizingsTableInfo.checkedRows.length === 0 || !online"
        class="p-button-info"
        @click="restoreSizings"
        >Restore Sizings</Button
      >
    </div>

    <SizingsTable
      v-model:checked-rows="sizingsTableInfo.checkedRows"
      v-model:limit="sizingsTableInfo.limit"
      v-model:offset="sizingsTableInfo.offset"
      v-model:sort="sizingsTableInfo.sort"
      :sizings="sizingsQuery?.sizings || []"
      :render-links="false"
      :sizingsAggregateCount="
        sizingsQuery?.sizings_aggregate?.aggregate?.count || 0
      "
    />
    <Divider class="py-4" />
    <div class="flex justify-content-between align-items-center">
      <h2>Deleted Scenarios</h2>
      <Button
        :disabled="isRestoreScenariosDisabled"
        class="p-button-info"
        @click="restoreScenarios"
        >Restore Scenarios</Button
      >
    </div>
    <ScenariosTable
      v-model:checked-rows="scenariosTableInfo.checkedRows"
      v-model:limit="scenariosTableInfo.limit"
      v-model:offset="scenariosTableInfo.offset"
      v-model:order-by="scenariosTableInfo.orderBy"
      :render-links="false"
      :scenarios="scenariosQuery?.scenarios || []"
      :scenariosAggregateCount="
        scenariosQuery?.scenarios_aggregate?.aggregate?.count || 0
      "
    />
    <Divider class="py-4" />
    <div class="flex justify-content-between align-items-center">
      <h2>Deleted Performance Evaluations</h2>
      <Button
        :disabled="isRestorePerfEvalsDisabled"
        class="p-button-info"
        @click="restorePerfEvals"
        >Restore Performance Evaluations</Button
      >
    </div>
    <PerfevalsTable
      v-model:checked-rows="perfEvalsTableInfo.checkedRows"
      v-model:limit="perfEvalsTableInfo.limit"
      v-model:offset="perfEvalsTableInfo.offset"
      v-model:order-by="perfEvalsTableInfo.orderBy"
      :perfevals="perfEvalsQuery?.performance_evaluations || []"
      :perfevalsAggregateCount="
        perfEvalsQuery?.performance_evaluations_aggregate?.aggregate?.count || 0
      "
      :render-links="false"
    />
  </div>
</template>

<script setup lang="ts">
import type {
  Scenarios_Order_By,
  Performance_Evaluations_Order_By,
  SizingsWithScenarioIdsQuery,
  ScenariosQuery,
  PerformanceEvaluationsQuery
} from "gql/api";
import {
  useInsertPerformanceEvaluationRevisionMutation,
  useInsertScenarioRevisionMutation,
  useInsertSizingsRevisionMutation,
  useSizingsWithScenarioIdsQuery,
  useScenariosQuery,
  usePerformanceEvaluationsQuery
} from "gql/api";
import { reactive, computed, ref } from "vue";
import SizingsTable from "@/components/sizings/SizingsTable.vue";
import ScenariosTable from "@/components/sizings/ScenariosTable.vue";
import PerfevalsTable from "@/components/sizings/PerfevalsTable.vue";
import { useToast } from "primevue/usetoast";
import useForm from "@/composables/forms/useForm";
import { omit } from "@/utils/utils";
import type { OperationResult } from "@urql/core";
import type { InputData, Sizing } from "@/composables/forms/formTypes";
import SizingsAdvancedSearch from "@/components/sizings/SizingsAdvancedSearch.vue";
import { useOnline } from "@vueuse/core";
import { useDeletedSizingsAdvancedSearch } from "@/composables/advancedSearch/sizings";
import { type SortMeta } from "@/composables/primevue/tables";

const online = useOnline();

const {
  sizingsWhere,
  sizingsSearch,
  scenariosWhere,
  perfevalsWhere,
  sizingsOrderBy,
  scenariosOrderBy,
  perfevalsOrderBy
} = useDeletedSizingsAdvancedSearch();

type TableInfo = {
  limit: number;
  offset: number;
};

const sizingsTableInfo: TableInfo & {
  checkedRows: SizingsWithScenarioIdsQuery["sizings"];
  sort: SortMeta[];
} = reactive({
  limit: 10,
  offset: 0,
  sort: [],
  checkedRows: []
});
const scenariosTableInfo: TableInfo & {
  checkedRows: ScenariosQuery["scenarios"];
  orderBy: Scenarios_Order_By[];
} = reactive({
  limit: 10,
  offset: 0,
  orderBy: [],
  checkedRows: []
});
const perfEvalsTableInfo: TableInfo & {
  checkedRows: PerformanceEvaluationsQuery["performance_evaluations"];
  orderBy: Performance_Evaluations_Order_By[];
} = reactive({
  limit: 10,
  offset: 0,
  orderBy: [],
  checkedRows: []
});

function getDisabledPerfEvalRows(
  perfevals: PerformanceEvaluationsQuery["performance_evaluations"]
) {
  return perfevals.filter(
    perfeval =>
      perfeval.r_scenario?.journal_deleted_at !== null ||
      perfeval.r_scenario?.sizing?.journal_deleted_at !== null
  );
}

function getDisabledScenarioRows(scenarios: ScenariosQuery["scenarios"]) {
  return scenarios.filter(
    scenario => scenario.sizing?.journal_deleted_at !== null
  );
}
/**
 * All Data Queries
 */

const { data: sizingsQuery, executeQuery: refetchSizings } =
  useSizingsWithScenarioIdsQuery({
    variables: computed(() => ({
      limit: sizingsTableInfo.limit,
      offset: sizingsTableInfo.offset,
      order_by: sizingsOrderBy,
      withAggregate: true,
      where: {
        journal_deleted_at: { _is_null: false },
        ...sizingsWhere.value
      }
    }))
  });
const { data: scenariosQuery, executeQuery: refetchScenarios } =
  useScenariosQuery({
    variables: computed(() => ({
      limit: scenariosTableInfo.limit,
      offset: scenariosTableInfo.offset,
      order_by: scenariosOrderBy,
      withAggregate: true,
      where: {
        _or: [
          { journal_deleted_at: { _is_null: false } },
          { sizing: { journal_deleted_at: { _is_null: false } } }
        ],
        ...scenariosWhere.value
      }
    }))
  });
const { data: perfEvalsQuery, executeQuery: refetchPerfEvals } =
  usePerformanceEvaluationsQuery({
    variables: computed(() => ({
      limit: perfEvalsTableInfo.limit,
      offset: perfEvalsTableInfo.offset,
      order_by: perfevalsOrderBy,
      withAggregate: true,
      where: {
        _or: [
          { journal_deleted_at: { _is_null: false } },
          { scenario: { journal_deleted_at: { _is_null: false } } },
          { scenario: { sizing: { journal_deleted_at: { _is_null: false } } } }
        ],
        ...perfevalsWhere.value
      }
    }))
  });

/**
 * On Restore Functions
 */

const toaster = useToast();
const { executeMutation: insertSizingRevision } =
  useInsertSizingsRevisionMutation();
const { executeMutation: insertScenarioRevision } =
  useInsertScenarioRevisionMutation();
const { executeMutation: insertPervevalRevision } =
  useInsertPerformanceEvaluationRevisionMutation();

const isRestorePerfEvalsDisabled = computed(() => {
  if (perfEvalsTableInfo.checkedRows.length === 0 || !online.value) {
    return true;
  }
  const disabledRows = getDisabledPerfEvalRows(perfEvalsTableInfo.checkedRows);
  if (disabledRows.length > 0) {
    toaster.add({
      severity: "error",
      summary: "Cannot Restore Performance Evaluation(s)",
      detail: `Performance Evaluation(s) with deleted scenarios/sizings cannot be restored.`
    });
  }
  return disabledRows.length > 0;
});

const isRestoreScenariosDisabled = computed(() => {
  if (scenariosTableInfo.checkedRows.length === 0 || !online.value) {
    return true;
  }
  const disabledRows = getDisabledScenarioRows(scenariosTableInfo.checkedRows);
  if (disabledRows.length > 0) {
    toaster.add({
      severity: "error",
      summary: "Cannot Restore Scenario(s)",
      detail: `Scenario(s) with deleted sizings cannot be restored.`
    });
  }
  return disabledRows.length > 0;
});

async function restoreSizings() {
  const undeletedSizings = sizingsTableInfo.checkedRows.map(sizing => {
    const form = useForm(ref(sizing as Sizing));
    form.update({ journal_deleted_at: null });

    const ignoredSizingFields = [
      "parent_sizing",
      "author",
      "well",
      "customer",
      "scenarios"
    ];

    return omit(form.prepare(), (_, k) => ignoredSizingFields.includes(k));
  });
  const result = await insertSizingRevision({
    objects: undeletedSizings
  });

  popToast("Sizings", result);

  if (result.data) {
    sizingsTableInfo.checkedRows = [];
    refetchSizings();
  }
}

async function restoreScenarios() {
  const undeletedScenarios = scenariosTableInfo.checkedRows.map(scenario => {
    const form = useForm(ref(scenario as InputData));
    form.update({ journal_deleted_at: null });

    const ignoredScenarioFields = [
      "author",
      "correlations",
      "equipment",
      "parameters",
      "well_properties",
      "sizing"
    ];

    return omit(form.prepare(), (_, k) => ignoredScenarioFields.includes(k));
  });
  const result = await insertScenarioRevision({
    objects: undeletedScenarios
  });

  popToast("Scenarios", result);

  if (result.data) {
    scenariosTableInfo.checkedRows = [];
    refetchScenarios();
  }
}

async function restorePerfEvals() {
  const undeletedPerfevals = perfEvalsTableInfo.checkedRows.map(perfeval => {
    const form = useForm(ref(perfeval));
    form.update({ journal_deleted_at: null });

    return form.prepare();
  });

  const result = await insertPervevalRevision({
    update_performance_evaluations: true,
    update_pumps: false,
    performance_evaluations_revisions: undeletedPerfevals,
    performance_evaluations_xref_pumps_revisions: []
  });

  popToast("Performance Evaluations", result);

  if (result.data) {
    perfEvalsTableInfo.checkedRows = [];
    refetchPerfEvals();
  }
}

function popToast(title: string, result: OperationResult) {
  if (result.data) {
    toaster.add({
      closable: true,
      summary: title + " Restored",
      severity: "success",
      life: 5000
    });
  }

  if (result.error) {
    toaster.add({
      closable: true,
      summary: title + " Restoration Error",
      detail: result.error.message,
      severity: "warn"
    });
  }
}
</script>
<style lang="scss">
.p-highlight {
  a {
    color: white;
  }
}
</style>
